// Состояние новой главной (business_apps#1486 §7): география, недавние,
// фасеты, новые чипы-фильтры и размер парка. Список устройств живёт в
// devicesNew (features/Devices/devicesSlice).

import { createSlice } from '@reduxjs/toolkit'
import { fetchDevices } from 'features/Devices/devicesSlice'
import { updateServiceNote } from 'features/DeviceServiceNote/model/updateServiceNote'
import { EMPTY_HOME_FILTERS, isUnfilteredDevicesRequest } from './lib'
import { STORAGE_KEYS } from './constants'
import {
  fetchGeography,
  fetchRecent,
  fetchFacets,
  facetsKey,
  updateUserSettings,
} from './services'

const readFleetSizeCache = () => {
  try {
    const value = Number(window.sessionStorage.getItem(STORAGE_KEYS.fleetSize))
    return Number.isFinite(value) && value > 0 ? value : null
  } catch (error) {
    return null
  }
}

const writeFleetSizeCache = (value) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEYS.fleetSize, String(value))
  } catch (error) {
    // storage недоступен
  }
}

const initialState = {
  geography: { data: null, status: 'idle', error: null },
  recent: { list: null, total: 0, status: 'idle', error: null },
  facets: { data: null, status: 'idle', error: null, chip: null, key: null },
  filters: EMPTY_HOME_FILTERS,
  // meta.total без фильтров — порядок блоков; sessionStorage спасает от
  // прыжка компоновки на второй загрузке у крупных парков.
  fleetSize: readFleetSizeCache(),
  cardsExpanded: false,
  variantUpdating: false,
}

const homeSlice = createSlice({
  name: 'homeV2',
  initialState,
  reducers: {
    setHomeFilter: (state, { payload: { name, values } }) => {
      state.filters = { ...state.filters, [name]: values || [] }
    },
    clearHomeFilters: (state) => {
      state.filters = EMPTY_HOME_FILTERS
    },
    setCardsExpanded: (state, { payload }) => {
      state.cardsExpanded = Boolean(payload)
    },
    clearFacets: (state) => {
      state.facets = initialState.facets
    },
    // Уход со страницы: фильтры и раскрытие сбрасываются, как у старого варианта.
    resetHome: (state) => {
      state.filters = EMPTY_HOME_FILTERS
      state.cardsExpanded = false
      state.facets = initialState.facets
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGeography.pending, (state) => {
        state.geography.status = 'loading'
        state.geography.error = null
      })
      .addCase(fetchGeography.fulfilled, (state, { payload }) => {
        state.geography.status = 'succeeded'
        state.geography.data = payload
      })
      .addCase(fetchGeography.rejected, (state, { payload, error }) => {
        state.geography.status = 'failed'
        state.geography.error = payload || error?.message || null
      })
      .addCase(fetchRecent.pending, (state) => {
        state.recent.status = 'loading'
        state.recent.error = null
      })
      .addCase(fetchRecent.fulfilled, (state, { payload }) => {
        state.recent.status = 'succeeded'
        state.recent.list = payload.devices
        state.recent.total = payload.total
      })
      .addCase(fetchRecent.rejected, (state, { payload, error }) => {
        state.recent.status = 'failed'
        state.recent.error = payload || error?.message || null
      })
      // Ответ принимается, только если это последний запрос: при быстрой смене
      // чипов старые фасеты не должны перетереть новые.
      .addCase(fetchFacets.pending, (state, { meta }) => {
        state.facets = {
          data: null,
          status: 'loading',
          error: null,
          chip: meta.arg?.chip || null,
          key: facetsKey(meta.arg?.params),
          requestId: meta.requestId,
        }
      })
      .addCase(fetchFacets.fulfilled, (state, { payload, meta }) => {
        if (state.facets.requestId !== meta.requestId) return
        state.facets.status = 'succeeded'
        state.facets.data = payload
      })
      .addCase(fetchFacets.rejected, (state, { payload, error, meta }) => {
        if (state.facets.requestId !== meta.requestId) return
        state.facets.status = 'failed'
        state.facets.error = payload || error?.message || null
      })
      .addCase(updateUserSettings.pending, (state) => {
        state.variantUpdating = true
      })
      .addCase(updateUserSettings.fulfilled, (state) => {
        state.variantUpdating = false
      })
      .addCase(updateUserSettings.rejected, (state) => {
        state.variantUpdating = false
      })
      // Заметка, исправленная карандашом в строке «Недавних», видна сразу:
      // devicesSlice обновляет только основной список.
      .addCase(updateServiceNote.fulfilled, (state, { payload }) => {
        const device = state.recent.list?.find(
          ({ uuid }) => uuid === payload?.deviceUuid
        )
        if (device) device.alias = payload.serviceNote
      })
      .addCase(fetchDevices.fulfilled, (state, { payload, meta }) => {
        if (!isUnfilteredDevicesRequest(meta?.arg)) return
        const total = Number(payload?.meta?.total)
        if (!Number.isFinite(total)) return
        state.fleetSize = total
        writeFleetSizeCache(total)
      })
  },
})

export const {
  setHomeFilter,
  clearHomeFilters,
  setCardsExpanded,
  clearFacets,
  resetHome,
} = homeSlice.actions

export default homeSlice.reducer
